import {
  documentToReactComponents,
  Options,
} from "@contentful/rich-text-react-renderer";
import { Document, INLINES } from "@contentful/rich-text-types";
import LoadingSpinner from "app/components/LoadingSpinner/LoadingSpinner";
import { createContentfulEntryUrl } from "app/helpers/contentfulHelper";
import { LanguageSelectionHelper } from "app/helpers/languageSelectionHelper";
import { landingPage } from "app/utils/const";
import ENV from "config";
import { useEffect, useState } from "react";
import "../PromotionalPage.scss";

/**
 * PromoPageSEOBlurb Component
 * ---------------------------
 * This logic will alter the links displayed in the SEO blurb
 * when we fetch the links from contentful they come to us as
 * https://www.3et.com/link/to/sport/
 * logic below will prepend the current environment to the url ie
 * on bpmdev we will have https://bpmdev.3et.com/sport/basketball/?filter=popular
 * locally we will have http://localhost:3000/sport/basketball/?filter=popular
 * 
 * this is to help QA when testing on other environments as well 
 * as it working locally when developing, marketing now can update contentful and UI will 
 * alter the link as required

 */
function rewriteLinkForEnv(url: string): string {
  if (typeof window === "undefined") return url;
  if (ENV.PRODUCTION === "TRUE") return url;

  try {
    const parsedUrl = new URL(url);
    if (parsedUrl.hostname) {
      parsedUrl.hostname = window.location.hostname;
      parsedUrl.port = window.location.port;
      parsedUrl.protocol = window.location.protocol;
      return parsedUrl.toString();
    }
  } catch (err) {
    console.warn("Couldn't rewrite the link:", url);
  }

  return url;
}

const renderOptions = (): Options => {
  return {
    renderNode: {
      [INLINES.HYPERLINK]: (node, children) => {
        // Get the original URL from the link node
        const originalUrl = node.data.uri;
        // Fix the URL if needed (like changing domains)
        const fixedUrl = rewriteLinkForEnv(originalUrl);
        // Show the link with the updated URL and styles
        return (
          <a
            href={fixedUrl}
            className="promo-seo__link"
            target="_blank"
            rel="noopener noreferrer"
          >
            {children}
          </a>
        );
      },
    },
  };
};

function PromoPageSEOBlurb() {
  const [isExpanded, setIsExpanded] = useState(false);
  const [fullDoc, setFullDoc] = useState<Document | null>(null);
  const [loading, setLoading] = useState(true);
  const [locale, setLocale] = useState("en-US");

  const spaceId = landingPage.CONTENTFUL_SPACE_ID;
  const accessToken = landingPage.CONTENTFUL_ACCESS_TOKEN;
  const environment = landingPage.CONTENTFUL_ENVIRONMENT_MASTER;
  const entryId = landingPage.CONTENTFUL_ENTRY_ID;

  useEffect(() => {
    if (LanguageSelectionHelper.checkSelectedLanguage().includes("es")) {
      setLocale("es");
    }
  }, []);

  useEffect(() => {
    async function fetchData() {
      try {
        // Use the helper function to create the entry URL
        const url = createContentfulEntryUrl({
          spaceId,
          environment,
          accessToken,
          entryId,
          locale,
        });
        const response = await fetch(url);
        const data = await response.json();

        setFullDoc(data.fields.seoBlurb as Document);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching SEO blurb:", error);
        setLoading(false);
      }
    }

    fetchData();
  }, [locale, entryId, spaceId, environment, accessToken]);

  const toggleContent = () => setIsExpanded(prev => !prev);

  if (loading)
    return (
      <p className="loading-text">
        <LoadingSpinner />
      </p>
    );
  if (!fullDoc) return <p className="error-text">Content not available.</p>;

  const firstBlocks: any[] = [];
  for (const block of fullDoc.content) {
    firstBlocks.push(block);
    if (block.nodeType === "paragraph") break; // stop after first paragraph
  }

  const firstPart: Document = {
    ...fullDoc,
    content: firstBlocks,
  };

  const restPart: Document = {
    ...fullDoc,
    content: fullDoc.content.slice(firstBlocks.length),
  };

  return (
    <section className="promo-SEO-container">
      <div className="promo-SEO-text">
        {documentToReactComponents(firstPart, renderOptions())}
        {isExpanded && documentToReactComponents(restPart, renderOptions())}
      </div>
      {fullDoc.content.length > firstBlocks.length && (
        <div className="read-more-toggle">
          <div onClick={toggleContent}>
            {isExpanded ? "Read Less" : "Read More"}
          </div>
        </div>
      )}
    </section>
  );
}

export default PromoPageSEOBlurb;
